import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonContent,
  IonButtons,
  IonMenuButton,
  IonButton,
  IonIcon,
  IonSkeletonText,
  IonRefresher,
  IonRefresherContent,
  IonBadge,
  useIonToast,
} from '@ionic/react';
import {
  search,
  settingsOutline,
  personAdd,
  ellipsisHorizontal,
  shieldCheckmark,
  people,
  chevronBack,
  chevronForward,
  close,
  toggleOutline,
  keyOutline,
  createOutline,
  peopleOutline,
  checkmarkCircle,
  ellipseOutline,
  informationCircleOutline,
  alertCircleOutline,
} from 'ionicons/icons';
import { format, parseISO } from 'date-fns';
import { ro } from 'date-fns/locale';
import {
  useAdminUsers,
  useCreateUser,
  useToggleUserActive,
  useResetUserPassword,
  useUpdateUserRoles,
  useUserScopes,
  useUpdateUserScopes,
} from '@/hooks/useAdmin';
import { useGroups } from '@/hooks/useGroups';
import { useSectors } from '@/hooks/useSectors';
import { usePermission } from '@/hooks/usePermission';
import { GROUP_CATEGORY_COLORS, GROUP_CATEGORY_LABELS, USER_ROLE_LABELS } from '@/utils/labels';
import type { AdminUser, AdminUserFilters, AdminUserFormData } from '@/types/admin';
import type { Group } from '@/types/group';
import type { Sector } from '@/types/sector';
import ConfirmDialog from '@/components/shared/ConfirmDialog';

const SCOPED_ROLES = new Set(['group_leader', 'group_assistant']);
const SECTOR_SCOPED_ROLES = new Set(['sector_leader']);

function firstNameOf(user: AdminUser): string {
  return user.first_name?.trim() || user.full_name?.split(' ')[0] || 'Utilizatorul';
}

const ROLE_COLORS: Record<string, string> = {
  admin: '#dc2626',
  pastor: '#4f46e5',
  secretary: '#059669',
  data_entry: '#0d9488',
  spiritual_editor: '#be185d',
  sector_leader: '#16a34a',
  group_leader: '#d97706',
  group_assistant: '#0891b2',
  observer: '#7c3aed',
  viewer: '#64748b',
  communion_recorder: '#9f1239',
};

const EMPTY_USER_FORM: AdminUserFormData = { name: '', email: '', password: '', role: 'viewer' };

const UserManagementPage: React.FC = () => {
  const [searchText, setSearchText] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 25;
  const [presentToast] = useIonToast();

  // Read-only roles (e.g. observer) reach this page via users.view but hold
  // none of the management permissions — hide every mutation control so they
  // never see a button that would 403. Admin holds all of these.
  const { can, canAny } = usePermission();
  const canCreateUsers = can('users.create');
  const canEditUsers = can('users.edit');
  const canManageRoles = can('users.manage_roles');
  const canManageUsers = canAny(['users.edit', 'users.manage_roles']);

  // Add user modal state
  const [showAddModal, setShowAddModal] = useState(false);
  const [formData, setFormData] = useState<AdminUserFormData>(EMPTY_USER_FORM);
  const createUserMut = useCreateUser();

  // Row actions state
  const [actionMenuUserId, setActionMenuUserId] = useState<string | null>(null);
  const [actionMenuAnchor, setActionMenuAnchor] = useState<DOMRect | null>(null);
  const [toggleConfirmUser, setToggleConfirmUser] = useState<AdminUser | null>(null);
  const [resetPasswordUser, setResetPasswordUser] = useState<AdminUser | null>(null);
  const [tempPassword, setTempPassword] = useState<string | null>(null);
  const [editRoleUser, setEditRoleUser] = useState<AdminUser | null>(null);
  const [editRoleValue, setEditRoleValue] = useState('');

  // Scope assignment modal state
  const [scopeUser, setScopeUser] = useState<AdminUser | null>(null);
  const [scopeSelected, setScopeSelected] = useState<Set<string>>(new Set());
  const [scopeSearch, setScopeSearch] = useState('');

  const toggleActiveMut = useToggleUserActive();
  const resetPasswordMut = useResetUserPassword();
  const updateRolesMut = useUpdateUserRoles();
  const updateScopesMut = useUpdateUserScopes();

  // The scope dialog manages groups (group_leader/assistant) or sectors
  // (sector_leader) depending on the target user's role.
  const scopeMode: 'group' | 'sector' =
    scopeUser && SECTOR_SCOPED_ROLES.has(scopeUser.role) ? 'sector' : 'group';

  const { data: scopesData, isLoading: scopesLoading } = useUserScopes(scopeUser?.id);
  const { data: allGroups, isLoading: groupsLoading } = useGroups(
    scopeUser && scopeMode === 'group' ? { per_page: 200 } : undefined,
  );
  const { data: allSectors, isLoading: sectorsLoading } = useSectors();

  // When the scope modal opens (or the fetch returns), seed the selected set.
  useEffect(() => {
    if (scopeUser && scopesData) {
      setScopeSelected(new Set(
        SECTOR_SCOPED_ROLES.has(scopeUser.role) ? scopesData.sector_ids ?? [] : scopesData.group_ids,
      ));
    }
  }, [scopeUser, scopesData]);

  // Close the row-action menu on scroll/resize so it doesn't detach from its anchor.
  useEffect(() => {
    if (!actionMenuUserId) return;
    const close = () => { setActionMenuUserId(null); setActionMenuAnchor(null); };
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [actionMenuUserId]);

  const openRowMenu = (user: AdminUser, el: HTMLElement) => {
    if (actionMenuUserId === user.id) {
      setActionMenuUserId(null);
      setActionMenuAnchor(null);
    } else {
      setActionMenuUserId(user.id);
      setActionMenuAnchor(el.getBoundingClientRect());
    }
  };

  const closeRowMenu = () => {
    setActionMenuUserId(null);
    setActionMenuAnchor(null);
  };

  const filteredScopeGroups = useMemo<Group[]>(() => {
    const groups = allGroups?.data ?? [];
    const q = scopeSearch.trim().toLowerCase();
    if (!q) return groups;
    return groups.filter((g) => g.name.toLowerCase().includes(q));
  }, [allGroups, scopeSearch]);

  const filteredScopeSectors = useMemo<Sector[]>(() => {
    const sectors = allSectors ?? [];
    const q = scopeSearch.trim().toLowerCase();
    if (!q) return sectors;
    return sectors.filter((s) => s.name.toLowerCase().includes(q));
  }, [allSectors, scopeSearch]);

  const toggleGroupInSelection = (id: string) => {
    setScopeSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleOpenScopes = (user: AdminUser) => {
    setScopeSearch('');
    setScopeSelected(new Set(
      SECTOR_SCOPED_ROLES.has(user.role) ? user.scoped_sector_ids ?? [] : user.scoped_group_ids ?? [],
    ));
    setScopeUser(user);
  };

  const handleCloseScopes = () => {
    if (updateScopesMut.isPending) return;
    setScopeUser(null);
    setScopeSelected(new Set());
    setScopeSearch('');
  };

  const handleSaveScopes = async () => {
    if (!scopeUser) return;
    const isSector = scopeMode === 'sector';
    try {
      await updateScopesMut.mutateAsync({
        id: scopeUser.id,
        ...(isSector
          ? { sectorIds: Array.from(scopeSelected) }
          : { groupIds: Array.from(scopeSelected) }),
      });
      void presentToast({
        message: scopeSelected.size === 0
          ? (isSector ? 'Sectoare eliminate.' : 'Grupuri eliminate.')
          : isSector
            ? `${scopeSelected.size} ${scopeSelected.size === 1 ? 'sector atribuit' : 'sectoare atribuite'}.`
            : `${scopeSelected.size} ${scopeSelected.size === 1 ? 'grup atribuit' : 'grupuri atribuite'}.`,
        duration: 2000, color: 'success', position: 'bottom',
      });
      handleCloseScopes();
    } catch {
      void presentToast({
        message: isSector ? 'Eroare la salvarea sectoarelor.' : 'Eroare la salvarea grupurilor.',
        duration: 2200, color: 'danger', position: 'bottom',
      });
    }
  };

  const filters: AdminUserFilters = {
    search: searchText || undefined,
    page: currentPage,
    page_size: pageSize,
  };

  const { data, isLoading, refetch } = useAdminUsers(filters);
  const users = data?.data ?? [];
  const totalCount = data?.total ?? 0;
  const totalPages = Math.ceil(totalCount / pageSize);

  const handleRefresh = async (event: CustomEvent) => {
    await refetch();
    event.detail.complete();
  };

  const handleOpenAdd = () => {
    setFormData(EMPTY_USER_FORM);
    setShowAddModal(true);
  };

  const handleCreateUser = async () => {
    if (!formData.name.trim()) {
      void presentToast({ message: 'Numele este obligatoriu.', duration: 2000, color: 'warning', position: 'bottom' });
      return;
    }
    if (!formData.email.trim()) {
      void presentToast({ message: 'Email-ul este obligatoriu.', duration: 2000, color: 'warning', position: 'bottom' });
      return;
    }
    try {
      await createUserMut.mutateAsync(formData);
      void presentToast({ message: 'Utilizator creat cu succes.', duration: 2000, color: 'success', position: 'bottom' });
      setShowAddModal(false);
    } catch {
      void presentToast({ message: 'Eroare la crearea utilizatorului.', duration: 2000, color: 'danger', position: 'bottom' });
    }
  };

  // Row action handlers
  const handleToggleActive = async () => {
    if (!toggleConfirmUser) return;
    try {
      await toggleActiveMut.mutateAsync(toggleConfirmUser.id);
      void presentToast({
        message: toggleConfirmUser.is_active ? 'Utilizator dezactivat.' : 'Utilizator activat.',
        duration: 2000, color: 'success', position: 'bottom',
      });
    } catch {
      void presentToast({ message: 'Eroare la schimbarea statusului.', duration: 2000, color: 'danger', position: 'bottom' });
    }
    setToggleConfirmUser(null);
  };

  const handleResetPassword = async () => {
    if (!resetPasswordUser) return;
    try {
      const result = await resetPasswordMut.mutateAsync(resetPasswordUser.id);
      setResetPasswordUser(null);
      setTempPassword(result.temporary_password);
    } catch {
      void presentToast({ message: 'Eroare la resetarea parolei.', duration: 2000, color: 'danger', position: 'bottom' });
      setResetPasswordUser(null);
    }
  };

  const handleUpdateRole = async () => {
    if (!editRoleUser || !editRoleValue) return;
    try {
      await updateRolesMut.mutateAsync({ id: editRoleUser.id, roles: [editRoleValue] });
      void presentToast({ message: 'Rol actualizat cu succes.', duration: 2000, color: 'success', position: 'bottom' });
    } catch {
      void presentToast({ message: 'Eroare la actualizarea rolului.', duration: 2000, color: 'danger', position: 'bottom' });
    }
    setEditRoleUser(null);
  };

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar className="crm-toolbar">
          <IonButtons slot="start">
            <IonMenuButton />
          </IonButtons>
          <IonTitle>Utilizatori</IonTitle>
          <IonButtons slot="end">
            <button className="crm-toolbar-icon"><IonIcon icon={search} /></button>
            <button className="crm-toolbar-icon"><IonIcon icon={settingsOutline} /></button>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent>
        <IonRefresher slot="fixed" onIonRefresh={handleRefresh}>
          <IonRefresherContent />
        </IonRefresher>

        <div className="crm-page-layout">
          {/* === LEFT STATS PANEL === */}
          <div className="crm-stats-panel">
            <div>
              <div className="crm-section-header">
                <h3 className="crm-section-title">Gestionare Conturi</h3>
                <p className="crm-section-subtitle">Utilizatori și roluri</p>
              </div>
            </div>

            <div className="crm-stat-card crm-animate-in">
              <div className="crm-stat-card__label">
                <span>Total Utilizatori</span>
                <div className="crm-stat-card__icon" style={{ backgroundColor: '#4f46e520', color: '#4f46e5' }}>
                  <IonIcon icon={people} />
                </div>
              </div>
              <div className="crm-stat-card__value">{totalCount}</div>
            </div>

            <div className="crm-stat-card crm-animate-in">
              <div className="crm-stat-card__label">
                <span>Roluri Disponibile</span>
                <div className="crm-stat-card__icon" style={{ backgroundColor: '#05966920', color: '#059669' }}>
                  <IonIcon icon={shieldCheckmark} />
                </div>
              </div>
              <div style={{ marginTop: 8 }}>
                {Object.entries(USER_ROLE_LABELS).map(([key, label]) => (
                  <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0' }}>
                    <div style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: ROLE_COLORS[key] ?? '#64748b' }} />
                    <span style={{ fontSize: 12, color: '#475569' }}>{label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* === MAIN CONTENT === */}
          <div className="crm-main-content">
            {/* Header row */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
              {canCreateUsers && (
                <IonButton size="small" style={{ '--border-radius': '8px' }} onClick={handleOpenAdd}>
                  <IonIcon icon={personAdd} slot="start" />
                  Adaugă Utilizator
                </IonButton>
              )}
            </div>

            {/* Search */}
            <div style={{ marginBottom: 16 }}>
              <input
                type="text"
                placeholder="Caută utilizatori..."
                value={searchText}
                onChange={(e) => { setSearchText(e.target.value); setCurrentPage(1); }}
                style={{
                  width: '100%', padding: '10px 0', fontSize: 14, fontWeight: 700,
                  fontFamily: "'Lato', sans-serif", color: '#1c1d21',
                  border: 'none', borderBottom: '1px solid #ececf2', outline: 'none',
                  background: 'transparent',
                }}
                onFocus={(e) => { (e.target.style.borderBottomColor = '#5e81f4'); }}
                onBlur={(e) => { (e.target.style.borderBottomColor = '#ececf2'); }}
              />
            </div>

            {/* Table */}
            <div className="crm-card" style={{ overflow: 'auto' }}>
              {isLoading ? (
                <div style={{ padding: 20 }}>
                  {[1, 2, 3, 4, 5].map((i) => (
                    <div key={i} style={{ padding: '12px 0', borderBottom: '1px solid #f1f5f9' }}>
                      <IonSkeletonText animated style={{ width: '60%', height: 16 }} />
                    </div>
                  ))}
                </div>
              ) : (
                <table className="crm-table">
                  <thead>
                    <tr>
                      <th style={{ minWidth: 200 }}>Utilizator</th>
                      <th style={{ width: 200 }}>Email</th>
                      <th style={{ width: 120 }}>Rol</th>
                      <th style={{ width: 80 }}>Status</th>
                      <th style={{ width: 140 }}>Ultima Conectare</th>
                      <th style={{ width: 50 }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((user) => {
                      const roleColor = ROLE_COLORS[user.role] ?? '#64748b';
                      return (
                        <tr key={user.id}>
                          <td>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                              <div
                                style={{
                                  width: 34,
                                  height: 34,
                                  borderRadius: '50%',
                                  backgroundColor: roleColor,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  color: '#fff',
                                  fontSize: 13,
                                  fontWeight: 700,
                                  flexShrink: 0,
                                }}
                              >
                                {(user.first_name?.[0] ?? user.email[0] ?? 'U').toUpperCase()}
                              </div>
                              <div className="crm-table__name">{user.full_name}</div>
                            </div>
                          </td>
                          <td style={{ fontSize: 13, color: '#475569' }}>{user.email}</td>
                          <td>
                            <span className="crm-badge" style={{ backgroundColor: `${roleColor}15`, color: roleColor }}>
                              {USER_ROLE_LABELS[user.role] ?? user.role}
                            </span>
                            {SCOPED_ROLES.has(user.role) && (
                              canManageRoles ? (
                                (user.scoped_group_ids?.length ?? 0) > 0 ? (
                                  <button
                                    type="button"
                                    className="scope-badge"
                                    onClick={() => handleOpenScopes(user)}
                                    title="Schimbă grupurile atribuite"
                                  >
                                    <IonIcon icon={peopleOutline} style={{ fontSize: 11 }} />
                                    {user.scoped_group_ids!.length} {user.scoped_group_ids!.length === 1 ? 'grup' : 'grupuri'}
                                    <IonIcon icon={createOutline} className="scope-badge__edit" />
                                  </button>
                                ) : (
                                  <button
                                    type="button"
                                    className="scope-badge scope-badge--empty"
                                    onClick={() => handleOpenScopes(user)}
                                    title="Niciun grup atribuit — apasă pentru a atribui"
                                  >
                                    <IonIcon icon={alertCircleOutline} style={{ fontSize: 12 }} />
                                    atribuie grupuri
                                    <IonIcon icon={chevronForward} className="scope-badge__edit" />
                                  </button>
                                )
                              ) : (
                                (user.scoped_group_ids?.length ?? 0) > 0 && (
                                  <span className="scope-badge" style={{ cursor: 'default' }}>
                                    <IonIcon icon={peopleOutline} style={{ fontSize: 11 }} />
                                    {user.scoped_group_ids!.length} {user.scoped_group_ids!.length === 1 ? 'grup' : 'grupuri'}
                                  </span>
                                )
                              )
                            )}
                            {SECTOR_SCOPED_ROLES.has(user.role) && (
                              canManageRoles ? (
                                (user.scoped_sector_ids?.length ?? 0) > 0 ? (
                                  <button
                                    type="button"
                                    className="scope-badge"
                                    onClick={() => handleOpenScopes(user)}
                                    title="Schimbă sectoarele atribuite"
                                  >
                                    <IonIcon icon={peopleOutline} style={{ fontSize: 11 }} />
                                    {user.scoped_sector_ids!.length} {user.scoped_sector_ids!.length === 1 ? 'sector' : 'sectoare'}
                                    <IonIcon icon={createOutline} className="scope-badge__edit" />
                                  </button>
                                ) : (
                                  <button
                                    type="button"
                                    className="scope-badge scope-badge--empty"
                                    onClick={() => handleOpenScopes(user)}
                                    title="Niciun sector atribuit — apasă pentru a atribui"
                                  >
                                    <IonIcon icon={alertCircleOutline} style={{ fontSize: 12 }} />
                                    atribuie sectoare
                                    <IonIcon icon={chevronForward} className="scope-badge__edit" />
                                  </button>
                                )
                              ) : (
                                (user.scoped_sector_ids?.length ?? 0) > 0 && (
                                  <span className="scope-badge" style={{ cursor: 'default' }}>
                                    <IonIcon icon={peopleOutline} style={{ fontSize: 11 }} />
                                    {user.scoped_sector_ids!.length} {user.scoped_sector_ids!.length === 1 ? 'sector' : 'sectoare'}
                                  </span>
                                )
                              )
                            )}
                          </td>
                          <td>
                            <IonBadge color={user.is_active ? 'success' : 'medium'} style={{ fontSize: 10 }}>
                              {user.is_active ? 'Activ' : 'Inactiv'}
                            </IonBadge>
                          </td>
                          <td style={{ fontSize: 12, color: '#94a3b8' }}>
                            {user.last_login_at
                              ? format(parseISO(user.last_login_at), 'd MMM yyyy, HH:mm', { locale: ro })
                              : '—'}
                          </td>
                          <td>
                            {canManageUsers && (
                              <button
                                className="crm-sort-button"
                                onClick={(e) => openRowMenu(user, e.currentTarget)}
                                aria-label="Mai multe acțiuni"
                              >
                                <IonIcon icon={ellipsisHorizontal} />
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}

              {!isLoading && users.length === 0 && (
                <div className="crm-empty-state">
                  <div className="crm-empty-state__icon"><IonIcon icon={people} /></div>
                  <h3 className="crm-empty-state__title">Niciun utilizator</h3>
                  <p className="crm-empty-state__text">Nu au fost creați utilizatori.</p>
                </div>
              )}

              {totalPages > 1 && (
                <div className="crm-pagination">
                  <button className="crm-pagination__btn" disabled={currentPage === 1} onClick={() => setCurrentPage(currentPage - 1)}>
                    <IonIcon icon={chevronBack} style={{ fontSize: 12 }} /> PREV
                  </button>
                  {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => i + 1).map((p) => (
                    <button
                      key={p}
                      className={`crm-pagination__num ${p === currentPage ? 'crm-pagination__num--active' : ''}`}
                      onClick={() => setCurrentPage(p)}
                    >
                      {p}
                    </button>
                  ))}
                  <button className="crm-pagination__btn" disabled={currentPage === totalPages} onClick={() => setCurrentPage(currentPage + 1)}>
                    NEXT <IonIcon icon={chevronForward} style={{ fontSize: 12 }} />
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ── Add User Modal ── */}
        {showAddModal && (
          <div className="logout-overlay" onClick={() => { if (!createUserMut.isPending) setShowAddModal(false); }}>
            <div className="logout-dialog" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 440, width: '90%', textAlign: 'left' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>Utilizator Nou</h3>
                <button onClick={() => setShowAddModal(false)} disabled={createUserMut.isPending} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}>
                  <IonIcon icon={close} style={{ fontSize: 22, color: '#94a3b8' }} />
                </button>
              </div>
              <div className="form-field" style={{ marginBottom: 16 }}>
                <label className="form-field__label">Nume complet *</label>
                <input className="form-input" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder="ex: Ion Popescu" autoFocus />
              </div>
              <div className="form-field" style={{ marginBottom: 16 }}>
                <label className="form-field__label">Email *</label>
                <input className="form-input" type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} placeholder="ex: ion@biserica.ro" />
              </div>
              <div className="form-field" style={{ marginBottom: 16 }}>
                <label className="form-field__label">Parolă</label>
                <input className="form-input" type="password" value={formData.password ?? ''} onChange={(e) => setFormData({ ...formData, password: e.target.value })} placeholder="Lasă gol pentru parolă generată automat" />
              </div>
              <div className="form-field" style={{ marginBottom: 24 }}>
                <label className="form-field__label">Rol</label>
                <select className="form-input" value={formData.role} onChange={(e) => setFormData({ ...formData, role: e.target.value })}>
                  {Object.entries(USER_ROLE_LABELS).map(([key, label]) => (
                    <option key={key} value={key}>{label}</option>
                  ))}
                </select>
                {SCOPED_ROLES.has(formData.role) && (
                  <div className="scope-hint">
                    <IonIcon icon={informationCircleOutline} />
                    <span>
                      După ce creezi contul, deschide „Atribuie Grupuri" din meniul rândului
                      — altfel utilizatorul nu va vedea niciun grup.
                    </span>
                  </div>
                )}
                {SECTOR_SCOPED_ROLES.has(formData.role) && (
                  <div className="scope-hint">
                    <IonIcon icon={informationCircleOutline} />
                    <span>
                      După ce creezi contul, deschide „Atribuie Sectoare" din meniul rândului
                      — altfel responsabilul nu va vedea nicio persoană.
                    </span>
                  </div>
                )}
              </div>
              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                <button className="form-btn form-btn--secondary" onClick={() => setShowAddModal(false)} disabled={createUserMut.isPending}>Anulează</button>
                <button className="form-btn form-btn--primary" onClick={handleCreateUser} disabled={createUserMut.isPending}>
                  {createUserMut.isPending && <span className="login-spinner" style={{ width: 14, height: 14, borderWidth: 2 }} />}
                  {createUserMut.isPending ? 'Se creează...' : 'Creează Utilizator'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── Change Role Modal ── */}
        {editRoleUser && (
          <div className="logout-overlay" onClick={() => { if (!updateRolesMut.isPending) setEditRoleUser(null); }}>
            <div className="logout-dialog" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 400, width: '90%', textAlign: 'left' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>Schimbă Rol</h3>
                <button onClick={() => setEditRoleUser(null)} disabled={updateRolesMut.isPending} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}>
                  <IonIcon icon={close} style={{ fontSize: 22, color: '#94a3b8' }} />
                </button>
              </div>
              <p style={{ fontSize: 13, color: '#64748b', margin: '0 0 16px' }}>
                Utilizator: <strong style={{ color: '#1c1d21' }}>{editRoleUser.full_name}</strong>
              </p>
              <div className="form-field" style={{ marginBottom: 24 }}>
                <label className="form-field__label">Rol nou</label>
                <select className="form-input" value={editRoleValue} onChange={(e) => setEditRoleValue(e.target.value)}>
                  {Object.entries(USER_ROLE_LABELS).map(([key, label]) => (
                    <option key={key} value={key}>{label}</option>
                  ))}
                </select>
              </div>
              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                <button className="form-btn form-btn--secondary" onClick={() => setEditRoleUser(null)} disabled={updateRolesMut.isPending}>Anulează</button>
                <button className="form-btn form-btn--primary" onClick={handleUpdateRole} disabled={updateRolesMut.isPending || editRoleValue === editRoleUser.role}>
                  {updateRolesMut.isPending && <span className="login-spinner" style={{ width: 14, height: 14, borderWidth: 2 }} />}
                  {updateRolesMut.isPending ? 'Se salvează...' : 'Salvează'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── Atribuie Grupuri Modal ── */}
        {scopeUser && (
          <div className="scope-dialog" onClick={handleCloseScopes} role="dialog" aria-modal="true">
            <div className="scope-dialog__panel" onClick={(e) => e.stopPropagation()}>
              <div className="scope-dialog__grip" aria-hidden="true" />

              <div className="scope-dialog__header">
                <h3 className="scope-dialog__title">
                  {scopeMode === 'sector' ? 'Atribuie sectoare pentru ' : 'Atribuie grupuri pentru '}
                  <em>{firstNameOf(scopeUser)}</em>
                </h3>
                <button
                  className="scope-dialog__close"
                  onClick={handleCloseScopes}
                  disabled={updateScopesMut.isPending}
                  aria-label="Închide"
                >
                  <IonIcon icon={close} style={{ fontSize: 18 }} />
                </button>
              </div>

              <p className="scope-dialog__help">
                {scopeMode === 'sector'
                  ? `${firstNameOf(scopeUser)} va vedea doar membrii, familiile și vizitele din sectoarele bifate aici. Poți schimba oricând selecția.`
                  : `${firstNameOf(scopeUser)} va vedea și gestiona doar grupurile bifate aici. Poți schimba oricând selecția.`}
              </p>

              {(scopeMode === 'sector' ? (allSectors?.length ?? 0) : (allGroups?.data.length ?? 0)) > 6 && (
                <div className="scope-dialog__search">
                  <input
                    type="text"
                    placeholder="Caută după nume..."
                    value={scopeSearch}
                    onChange={(e) => setScopeSearch(e.target.value)}
                    disabled={groupsLoading}
                    autoFocus={false}
                  />
                </div>
              )}

              <ul className="scope-dialog__list">
                {(scopesLoading || (scopeMode === 'sector' ? sectorsLoading : groupsLoading)) ? (
                  Array.from({ length: 4 }).map((_, i) => (
                    <li key={i} className="scope-dialog__skeleton" style={{ animationDelay: `${i * 80}ms` }} />
                  ))
                ) : scopeMode === 'sector' ? (
                  filteredScopeSectors.length === 0 ? (
                    <div className="scope-dialog__empty">
                      <h4>{scopeSearch ? 'Niciun sector găsit' : 'Niciun sector creat încă'}</h4>
                      <p>
                        {scopeSearch
                          ? 'Încearcă alt termen de căutare.'
                          : 'Creează sectoarele din meniul Sectoare, apoi revino aici.'}
                      </p>
                    </div>
                  ) : (
                    filteredScopeSectors.map((sector, i) => {
                      const selected = scopeSelected.has(sector.id);
                      return (
                        <li
                          key={sector.id}
                          className={`scope-dialog__row${selected ? ' scope-dialog__row--on' : ''}`}
                          style={{ animationDelay: `${Math.min(i, 8) * 40}ms` }}
                        >
                          <button type="button" onClick={() => toggleGroupInSelection(sector.id)} aria-pressed={selected}>
                            <span className="scope-dialog__rail" style={{ background: sector.color ?? '#8181a5' }} aria-hidden="true" />
                            <div className="scope-dialog__body">
                              <div className="scope-dialog__name">{sector.name}</div>
                              <div className="scope-dialog__meta">
                                <span>{sector.family_count} {sector.family_count === 1 ? 'familie' : 'familii'}</span>
                                <span className="scope-dialog__meta--dot" aria-hidden="true" />
                                <span>{sector.member_count} {sector.member_count === 1 ? 'membru' : 'membri'}</span>
                              </div>
                            </div>
                            <IonIcon
                              icon={selected ? checkmarkCircle : ellipseOutline}
                              className="scope-dialog__check"
                              aria-hidden="true"
                            />
                          </button>
                        </li>
                      );
                    })
                  )
                ) : filteredScopeGroups.length === 0 ? (
                  <div className="scope-dialog__empty">
                    <h4>{scopeSearch ? 'Niciun grup găsit' : 'Niciun grup creat încă'}</h4>
                    <p>
                      {scopeSearch
                        ? 'Încearcă alt termen de căutare.'
                        : 'Creează un grup din meniul Grupuri, apoi revino aici.'}
                    </p>
                  </div>
                ) : (
                  filteredScopeGroups.map((group, i) => {
                    const selected = scopeSelected.has(group.id);
                    const railColor = GROUP_CATEGORY_COLORS[group.category] ?? '#8181a5';
                    const categoryLabel = GROUP_CATEGORY_LABELS[group.category] ?? 'Grup';
                    const count = group.member_count ?? group.members_count ?? 0;
                    return (
                      <li
                        key={group.id}
                        className={`scope-dialog__row${selected ? ' scope-dialog__row--on' : ''}`}
                        style={{ animationDelay: `${Math.min(i, 8) * 40}ms` }}
                      >
                        <button type="button" onClick={() => toggleGroupInSelection(group.id)} aria-pressed={selected}>
                          <span className="scope-dialog__rail" style={{ background: railColor }} aria-hidden="true" />
                          <div className="scope-dialog__body">
                            <div className="scope-dialog__name">{group.name}</div>
                            <div className="scope-dialog__meta">
                              <span>{categoryLabel}</span>
                              <span className="scope-dialog__meta--dot" aria-hidden="true" />
                              <span>{count} {count === 1 ? 'membru' : 'membri'}</span>
                            </div>
                          </div>
                          <IonIcon
                            icon={selected ? checkmarkCircle : ellipseOutline}
                            className="scope-dialog__check"
                            aria-hidden="true"
                          />
                        </button>
                      </li>
                    );
                  })
                )}
              </ul>

              <div className="scope-dialog__footer">
                <span className="scope-dialog__count">
                  {scopeSelected.size === 0 ? (
                    scopeMode === 'sector' ? 'niciun sector selectat' : 'niciun grup selectat'
                  ) : (
                    <>
                      <strong>{scopeSelected.size}</strong>{' '}
                      {scopeMode === 'sector'
                        ? (scopeSelected.size === 1 ? 'sector selectat' : 'sectoare selectate')
                        : (scopeSelected.size === 1 ? 'grup selectat' : 'grupuri selectate')}
                    </>
                  )}
                </span>
                <button
                  className="scope-dialog__btn scope-dialog__btn--ghost"
                  onClick={handleCloseScopes}
                  disabled={updateScopesMut.isPending}
                >
                  Anulează
                </button>
                <button
                  className="scope-dialog__btn scope-dialog__btn--primary"
                  onClick={handleSaveScopes}
                  disabled={updateScopesMut.isPending || scopesLoading}
                >
                  {updateScopesMut.isPending && <span className="login-spinner" style={{ width: 12, height: 12, borderWidth: 2, marginRight: 6 }} />}
                  Salvează
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── Toggle Active Confirm ── */}
        <ConfirmDialog
          isOpen={!!toggleConfirmUser}
          title={toggleConfirmUser?.is_active ? 'Dezactivare Utilizator' : 'Activare Utilizator'}
          message={
            toggleConfirmUser?.is_active
              ? `Sigur doriți să dezactivați contul „${toggleConfirmUser?.full_name}"? Utilizatorul nu se va mai putea autentifica.`
              : `Sigur doriți să activați contul „${toggleConfirmUser?.full_name}"?`
          }
          confirmLabel={toggleConfirmUser?.is_active ? 'Dezactivează' : 'Activează'}
          variant={toggleConfirmUser?.is_active ? 'warning' : 'info'}
          isLoading={toggleActiveMut.isPending}
          onConfirm={handleToggleActive}
          onCancel={() => setToggleConfirmUser(null)}
        />

        {/* ── Reset Password Confirm ── */}
        <ConfirmDialog
          isOpen={!!resetPasswordUser}
          title="Resetare Parolă"
          message={`Sigur doriți să resetați parola pentru „${resetPasswordUser?.full_name}"? Sesiunile active vor fi revocate.`}
          confirmLabel="Resetează"
          variant="danger"
          isLoading={resetPasswordMut.isPending}
          onConfirm={handleResetPassword}
          onCancel={() => setResetPasswordUser(null)}
        />

        {/* ── Temp Password Display ── */}
        {tempPassword && (
          <div className="logout-overlay" onClick={() => setTempPassword(null)}>
            <div className="logout-dialog" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 400, width: '90%' }}>
              <div className="logout-dialog__icon" style={{ background: '#eef2ff', color: '#5e81f4' }}>
                <IonIcon icon={keyOutline} />
              </div>
              <h3 className="logout-dialog__title">Parolă Temporară</h3>
              <p className="logout-dialog__text" style={{ marginBottom: 8 }}>
                Parola temporară a fost generată. Comunicați-o utilizatorului:
              </p>
              <div
                style={{
                  background: '#f1f5f9', borderRadius: 8, padding: '12px 16px',
                  fontFamily: 'monospace', fontSize: 16, fontWeight: 700,
                  textAlign: 'center', letterSpacing: 2, color: '#0f172a',
                  userSelect: 'all', marginBottom: 16,
                }}
              >
                {tempPassword}
              </div>
              <div className="logout-dialog__actions">
                <button className="form-btn form-btn--primary" onClick={() => setTempPassword(null)}>
                  Am înțeles
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── Row-action dropdown (portaled to body to escape table overflow) ── */}
        {actionMenuUserId && actionMenuAnchor && (() => {
          const menuUser = users.find((u) => u.id === actionMenuUserId);
          if (!menuUser) return null;
          const menuWidth = 220;
          const top = Math.min(actionMenuAnchor.bottom + 4, window.innerHeight - 200);
          const right = Math.max(8, window.innerWidth - actionMenuAnchor.right);
          return createPortal(
            <>
              <div
                className="row-menu-backdrop"
                onClick={closeRowMenu}
                onTouchStart={closeRowMenu}
              />
              <div className="row-menu" style={{ top, right, width: menuWidth }} role="menu">
                {canManageRoles && (
                  <button
                    className="row-menu__item"
                    onClick={() => {
                      closeRowMenu();
                      setEditRoleUser(menuUser);
                      setEditRoleValue(menuUser.role);
                    }}
                  >
                    <IonIcon icon={createOutline} style={{ fontSize: 16, color: '#5e81f4' }} />
                    Schimbă rol
                  </button>
                )}
                {canManageRoles && (SCOPED_ROLES.has(menuUser.role) || SECTOR_SCOPED_ROLES.has(menuUser.role)) && (
                  <button
                    className="row-menu__item"
                    onClick={() => {
                      closeRowMenu();
                      handleOpenScopes(menuUser);
                    }}
                  >
                    <IonIcon icon={peopleOutline} style={{ fontSize: 16, color: '#0891b2' }} />
                    {SECTOR_SCOPED_ROLES.has(menuUser.role) ? 'Atribuie sectoare' : 'Atribuie grupuri'}
                  </button>
                )}
                {canEditUsers && (
                  <button
                    className="row-menu__item"
                    onClick={() => {
                      closeRowMenu();
                      setToggleConfirmUser(menuUser);
                    }}
                  >
                    <IonIcon icon={toggleOutline} style={{ fontSize: 16, color: menuUser.is_active ? '#d97706' : '#059669' }} />
                    {menuUser.is_active ? 'Dezactivează' : 'Activează'}
                  </button>
                )}
                {canEditUsers && (
                  <button
                    className="row-menu__item"
                    onClick={() => {
                      closeRowMenu();
                      setResetPasswordUser(menuUser);
                    }}
                  >
                    <IonIcon icon={keyOutline} style={{ fontSize: 16, color: '#dc2626' }} />
                    Resetează parola
                  </button>
                )}
              </div>
            </>,
            document.body,
          );
        })()}
      </IonContent>
    </IonPage>
  );
};

export default UserManagementPage;
